import React, { useEffect, useState } from 'react';
import { inspectionsApi, InspectionSummary } from '@/api/inspections';
import { templatesApi, type TemplateSummary } from '@/api/templates';
import { Modal, Field, Input, Select, Button } from '@/ui';

interface NewInspectionModalProps {
  onCreated: (inspection: InspectionSummary) => void;
  onClose: () => void;
}

const NewInspectionModal: React.FC<NewInspectionModalProps> = ({ onCreated, onClose }) => {
  const [address, setAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Template picker: only shown when the inspector has more than one template.
  // Defaults to whichever template is flagged as the default so the common
  // case (single-click through the form) still lands on their usual scaffold.
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [templateId, setTemplateId] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    templatesApi.list().then((rows) => {
      if (cancelled) return;
      setTemplates(rows);
      const preferred = rows.find((t) => t.isDefault) ?? rows[0];
      if (preferred) setTemplateId(preferred.id);
    }).catch(() => {
      // Non-fatal: falling back to server-side default is fine. The picker
      // simply won't render.
      if (!cancelled) setTemplates([]);
    });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      const chosen = templates?.find((t) => t.id === templateId);
      // Only send template_id when the user actually picked a non-default
      // template — for the default case, letting the backend resolve keeps
      // behaviour identical to before this feature.
      const overrideTemplate = chosen && !chosen.isDefault ? chosen.id : undefined;
      const result = await inspectionsApi.create({
        property_address: address.trim(),
        client_name: clientName.trim() || undefined,
        client_email: clientEmail.trim() || undefined,
        inspection_date: date,
        template_id: overrideTemplate,
      });
      if (result.success && result.data) {
        onCreated(result.data);
      } else {
        setError(result.error ?? 'Failed to create inspection');
      }
    } catch {
      setError('Failed to create inspection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New Inspection"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
            disabled={submitting || !address.trim()}
          >
            {submitting ? 'Creating…' : 'Start Inspection'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-sev-major-soft border border-sev-major/20 rounded-md text-body-sm text-sev-major-ink">
            {error}
          </div>
        )}

        <Field label="Property Address" htmlFor="new-inspection-address">
          <Input
            id="new-inspection-address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
            placeholder="123 Main St, City, State ZIP"
          />
        </Field>

        {templates && templates.length > 1 && (
          <Field label="Template" htmlFor="new-inspection-template">
            <Select
              id="new-inspection-template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Client Name" htmlFor="new-inspection-client" optional>
          <Input
            id="new-inspection-client"
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="John Smith"
          />
        </Field>

        <Field label="Client Email" htmlFor="new-inspection-email" optional>
          <Input
            id="new-inspection-email"
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="client@example.com"
          />
        </Field>

        <Field label="Inspection Date" htmlFor="new-inspection-date">
          <Input
            id="new-inspection-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </Field>

        {/* Hidden submit button so Enter on any field triggers the form. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
};

export default NewInspectionModal;
